import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext.tsx';
import { AlertTriangle, Loader2, Eye, EyeOff } from 'lucide-react';
import { CHIP_LOGO } from '../constants.ts';

const Login: React.FC = () => {
  const { signInWithGoogle, signInWithEmail, signUpWithEmail, loading, isConfigured } = useAuth();
  
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      if (isSignUp) {
        await signUpWithEmail(email, password);
      } else {
        await signInWithEmail(email, password);
      }
    } catch (err: any) {
      console.error(err);
      if (err.code === 'auth/invalid-credential' || err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password') {
        setError('Invalid credentials.');
      } else {
        setError('Authentication failed. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    try {
      await signInWithGoogle();
    } catch (err) {
      setError('Google Sign In failed.');
    }
  };

  if (!isConfigured) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white p-4">
         <div className="card max-w-md w-full p-8 text-center border-red-500/50">
           <AlertTriangle size={48} className="mx-auto text-red-500 mb-4" />
           <h2 className="text-xl font-bold mb-2 text-gray-900">Configuration Required</h2>
           <p className="text-gray-600">Please set up your Firebase keys in <code>lib/firebase.ts</code>.</p>
         </div>
      </div>
    );
  }

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-white text-gray-900">Loading...</div>;
  }

  return (
    <div className="flex min-h-screen bg-white text-gray-900">
      
      {/* Left Panel: Form (50%) - Enabled for md and up */}
      <div className="w-full md:w-1/2 flex flex-col justify-center px-8 sm:px-12 lg:px-20 bg-white relative z-10">
        <div className="max-w-[400px] w-full mx-auto">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-10">
            <img 
              src={CHIP_LOGO} 
              alt="Chip Logo" 
              className="w-10 h-10 rounded-lg object-contain" 
              referrerPolicy="no-referrer"
            />
            <span className="font-heading font-bold text-2xl text-gray-900">Chip AI Studio</span>
          </div>

          <h1 className="font-heading text-3xl font-bold text-gray-900 mb-2">
            {isSignUp ? 'Create an account' : 'Welcome back'}
          </h1>
          <p className="text-gray-500 mb-8">
            {isSignUp ? 'Start creating your on-brand assets today.' : 'Please enter your details to sign in.'}
          </p>

          {error && (
            <div className="mb-6 p-3 bg-red-50 text-red-600 border border-red-200 rounded-lg text-sm flex items-center gap-2">
              <AlertTriangle size={16} /> {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-1.5">Email Address</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-white border border-gray-300 text-gray-900 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-all"
                placeholder="name@company.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-900 mb-1.5">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-white border border-gray-300 text-gray-900 rounded-lg p-3 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent transition-all"
                  placeholder="••••••••"
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-900"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {!isSignUp && (
              <div className="flex items-center justify-between text-sm">
                <label className="flex items-center gap-2 cursor-pointer text-gray-600">
                  <input type="checkbox" className="accent-black w-4 h-4" />
                  Remember me
                </label>
                <a href="#" className="text-gray-900 font-medium hover:underline">Forgot password?</a>
              </div>
            )}

            <button 
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#FF0069] hover:bg-[#E0005E] text-white font-medium rounded-lg p-3 transition-colors flex items-center justify-center gap-2 h-12"
            >
              {isSubmitting ? <Loader2 className="animate-spin" size={20} /> : (isSignUp ? 'Create Account' : 'Sign In')}
            </button>
          </form>

          <div className="my-8 flex items-center gap-4">
            <div className="h-px bg-gray-200 flex-1"></div>
            <span className="text-xs text-gray-400 font-medium uppercase">Or</span>
            <div className="h-px bg-gray-200 flex-1"></div>
          </div>

          <button 
            onClick={handleGoogleSignIn}
            className="w-full py-3 bg-white border border-gray-300 text-gray-900 rounded-lg font-medium hover:bg-gray-50 transition-all flex items-center justify-center gap-3"
          >
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" className="w-5 h-5" />
            Sign in with Google
          </button>

          <p className="mt-8 text-center text-sm text-gray-500">
            {isSignUp ? "Already have an account?" : "Don't have an account?"}
            <button 
              onClick={() => { setError(null); setIsSignUp(!isSignUp); }}
              className="ml-2 font-semibold text-gray-900 hover:underline"
            >
              {isSignUp ? 'Login' : 'Sign up'}
            </button>
          </p>
        </div>
      </div>

      {/* Right Panel: Visual (50%) - Enabled for md and up */}
      <div className="hidden md:block w-1/2 relative bg-black">
         <video 
           autoPlay 
           muted 
           loop 
           playsInline
           className="absolute inset-0 w-full h-full object-cover"
         >
           <source src="https://cdn.midjourney.com/video/e1fef2fa-8901-4b51-a072-c354a81bd450/2.mp4" type="video/mp4" />
         </video>
      </div>
    </div>
  );
};

export default Login;